import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App.tsx";
import { ApiProvider } from "./api/ApiProvider";
import { isUnauthorized } from "./api/errors";
import { AuthProvider } from "./auth/AuthContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import "./styles/global.css";
import { ThemeProvider } from "./theme/ThemeContext";
import { ToastProvider } from "./toast/ToastContext";

const root = document.getElementById("root");
if (!root) throw new Error("root element missing");

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 401 means the session is gone — `realClient`'s `emitUnauthorized`
      // already fires on the very first failure (see authEvents.ts), which
      // clears `AuthContext` and sends `ProtectedRoute` back to /login on
      // the same render pass. Retrying it first would just burn the retry
      // delay showing a loading spinner for a page about to be redirected
      // away from; every other status still gets the one retry.
      retry: (failureCount, error) => (isUnauthorized(error) ? false : failureCount < 1),
      // The UI is event-driven, not polled (charter P-1) — real-time
      // correctness comes from the event subscription seam
      // (useContainerEventsSubscription), not from refetch-on-focus.
      refetchOnWindowFocus: false,
    },
  },
});

createRoot(root).render(
  <StrictMode>
    {/* Last-resort boundary: catches a crash in any provider above the
        router (theme, toast, auth, the API client itself) that a per-route
        boundary inside App can never reach because it hasn't mounted yet.
        No `key` here — there's no route to remount on, so recovery is
        "Try again" (re-render the same tree) or a full reload. */}
    <ErrorBoundary label="Comebye">
      <QueryClientProvider client={queryClient}>
        <ApiProvider>
          <ThemeProvider>
            <ToastProvider>
              <AuthProvider>
                <App />
              </AuthProvider>
            </ToastProvider>
          </ThemeProvider>
        </ApiProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
